(() => {
  if (customElements.get('distribution-stock')) return;

  const logos = {
    digikey: 'https://cdn.shopify.com/s/files/1/0560/3075/6927/files/Digikey-logo.png?v=1714781379',
    masterelectronics: 'https://cdn.shopify.com/s/files/1/0560/3075/6927/files/Master-logo.png?v=1714781380',
    arrow: 'https://cdn.shopify.com/s/files/1/0560/3075/6927/files/Arrow-logo.png?v=1714778530',
    arrowelectronics: 'https://cdn.shopify.com/s/files/1/0560/3075/6927/files/Arrow-logo.png?v=1714778530'
  };

  const distributorSites = {
    digikey: 'https://www.digikey.com/',
    masterelectronics: 'https://www.masterelectronics.com/',
    arrow: 'https://www.arrow.com/'
  };

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function webUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, window.location.origin);
      return ['https:', 'http:'].includes(url.protocol) ? url : null;
    } catch {
      return null;
    }
  }

  function stockRank(listing) {
    return typeof listing.quantity === 'number' && Number.isFinite(listing.quantity) && listing.quantity >= 0
      ? listing.quantity
      : -1;
  }

  function distributorKey(name) {
    const key = name.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (key === 'digikeyelectronics') return 'digikey';
    if (key === 'arrowelectronics') return 'arrow';
    return key;
  }

  function distributorPriority(name) {
    const key = distributorKey(name);
    return key === 'digikey' ? 0 : key === 'arrow' ? 2 : 1;
  }

  class DistributionStock extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.groups = this.querySelector('[data-stock-groups]');
      this.table = this.querySelector('[data-stock-table]');
      this.labels = [...this.querySelectorAll('.distribution-stock__table thead th')].map(header => header.textContent.trim());
      this.status = this.querySelector('[data-stock-status]');
      this.product = this.querySelector('[data-stock-product]');
      this.retry = this.querySelector('[data-stock-retry]');
      this.events = new AbortController();
      const options = { signal: this.events.signal };

      document.addEventListener('click', event => {
        const button = event.target.closest('[data-secondary-action="open-stocking-quick-view"]');
        if (!button) return;
        event.preventDefault();
        this.trigger = button;
        this.productUrl = button.dataset.productUrl;
        this.product.textContent = button.dataset.productTitle || '';
        if (!this.dialog.open) this.dialog.showModal();
        this.load();
      }, options);

      this.querySelector('[data-stock-close]').addEventListener('click', () => this.dialog.close(), options);
      this.retry.addEventListener('click', () => this.load(), options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {
          this.dialog.close();
        }
      }, options);
      this.dialog.addEventListener('close', () => {
        this.request?.abort();
        this.request = null;
        if (this.trigger?.isConnected) this.trigger.focus();
      }, options);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
    }

    async load() {
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      this.replaceGroups();
      this.table.hidden = true;
      this.groups.setAttribute('aria-busy', 'true');
      this.status.hidden = false;
      this.status.textContent = this.dataset.loading;
      this.retry.hidden = true;
      const timeout = setTimeout(() => request.abort(), 15000);

      try {
        const url = webUrl(this.productUrl);
        if (!url || url.origin !== window.location.origin) throw new Error('Invalid product URL');
        url.search = '';
        url.hash = '';
        url.searchParams.set('section_id', 'distribution-stock');
        const response = await fetch(url, { signal: request.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Stock request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const payload = html.querySelector('[data-distribution-payload]');
        const data = JSON.parse(payload?.textContent || 'null');
        if (!data || !Array.isArray(data.listings)) throw new Error('Stock data is unavailable');
        if (this.request !== request || !this.dialog.open) return;
        this.product.textContent = data.product || '';
        this.render(data.listings);
      } catch {
        if (this.request !== request || !this.dialog.open) return;
        this.status.textContent = this.dataset.error;
        this.retry.hidden = false;
      } finally {
        clearTimeout(timeout);
        if (this.request === request) this.groups.setAttribute('aria-busy', 'false');
      }
    }

    replaceGroups(content) {
      [...this.groups.tBodies].forEach(group => group.remove());
      if (content) this.groups.append(content);
    }

    render(listings) {
      const groups = new Map();
      for (const listing of listings) {
        if (!listing || typeof listing !== 'object') continue;
        const name = String(listing.distributor || '').trim() || this.dataset.distributor;
        const key = distributorKey(name);
        if (!groups.has(key)) groups.set(key, { name, listings: [] });
        groups.get(key).listings.push(listing);
      }
      this.status.hidden = groups.size > 0;
      this.status.textContent = groups.size ? '' : this.dataset.empty;
      const fragment = document.createDocumentFragment();
      const orderedGroups = [...groups.values()].sort((first, second) =>
        distributorPriority(first.name) - distributorPriority(second.name));
      for (const group of orderedGroups) {
        const body = element('tbody');
        group.listings.sort((first, second) => stockRank(second) - stockRank(first));
        group.listings.forEach((listing, index) => {
          const row = this.card(listing, group.name);
          if (index === 0) {
            row.classList.add('distribution-stock__group-start');
            const distributor = element('th', 'distribution-stock__distributor-cell');
            distributor.scope = 'rowgroup';
            distributor.rowSpan = group.listings.length;
            distributor.append(this.media(listing, group.name));
            row.prepend(distributor);
          }
          body.append(row);
        });
        fragment.append(body);
      }
      this.table.hidden = groups.size === 0;
      this.replaceGroups(fragment);
    }

    media(listing, distributor) {
      const website = webUrl(distributorSites[distributorKey(distributor)]) || webUrl(listing.url);
      const media = element(website ? 'a' : 'div', 'distribution-stock__media');
      if (website) {
        media.href = `${website.origin}/`;
        media.target = '_blank';
        media.rel = 'noopener noreferrer';
        media.setAttribute('aria-label', `${distributor}. ${this.dataset.newWindow}`);
      }
      const logo = logos[distributorKey(distributor)];
      const imageUrl = webUrl(logo) || webUrl(listing.image);
      const placeholder = element('span', 'distribution-stock__distributor-name', distributor);
      media.append(placeholder);
      if (imageUrl) {
        const image = element('img');
        image.src = imageUrl.href;
        image.alt = distributor;
        image.width = 120;
        image.height = 40;
        image.addEventListener('load', () => { placeholder.hidden = true; }, { once: true });
        image.addEventListener('error', () => { image.remove(); placeholder.hidden = false; }, { once: true });
        media.append(image);
      }
      return media;
    }

    card(listing, distributor) {
      const row = element('tr', 'distribution-stock__row');
      const part = element('td', 'distribution-stock__part-cell', listing.sku || this.product.textContent);
      const packagingValue = typeof listing.packaging === 'string' ? listing.packaging.trim() : '';
      const packaging = element('td', 'distribution-stock__packaging-cell', packagingValue || this.dataset.packagingUnknown);
      const stock = element('td', 'distribution-stock__stock-cell');
      const quantity = listing.quantity;
      const known = typeof quantity === 'number' && Number.isFinite(quantity) && quantity >= 0;
      stock.append(element('span', 'distribution-stock__stock-number', known ? new Intl.NumberFormat(document.documentElement.lang || 'en').format(quantity) : '—'));
      stock.append(element('span', 'distribution-stock__visually-hidden', known ? (quantity > 0 ? this.dataset.stock : this.dataset.outOfStock) : this.dataset.unknown));
      const action = element('td', 'distribution-stock__action');
      const url = webUrl(listing.url);
      if (url) {
        const link = element('a', 'distribution-stock__link', this.dataset.visit);
        link.href = url.href;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.title = url.href;
        link.setAttribute('aria-label', `${this.dataset.visit}: ${distributor}, ${listing.sku || this.product.textContent}. ${this.dataset.newWindow}`);
        action.append(link);
      } else {
        action.append(element('span', 'distribution-stock__no-url', this.dataset.noUrl));
      }
      [part, packaging, stock].forEach((cell, index) => {
        const label = element('span', 'distribution-stock__mobile-label', this.labels[index + 1]);
        label.setAttribute('aria-hidden', 'true');
        cell.prepend(label);
      });
      row.append(part, packaging, stock, action);
      return row;
    }
  }

  customElements.define('distribution-stock', DistributionStock);
})();
